// src/components/plm/HomeDashboard.jsx
import styles from "../../styles/PLMApp.module.css";
import { PLM_CATEGORIES } from "../../data/constants";
import { stepDocId } from "../../utils/utils";

export default function HomeDashboard({
  steps,
  onNavigate,
  supplierId,
  orderProfile,
}) {
  const allStepKeys = PLM_CATEGORIES.flatMap((cat) =>
    cat.steps.map((_, i) => stepDocId(supplierId, cat.id, i)),
  );

  const done = allStepKeys.filter((k) => steps[k]?.status === "done").length;
  const applicable = allStepKeys.filter(
    (k) => steps[k]?.status !== "not_needed",
  ).length;
  const pct = applicable > 0 ? Math.round((done / applicable) * 100) : 0;
  const inProgress = allStepKeys.filter(
    (k) => steps[k]?.status === "in_progress",
  ).length;
  const notNeeded = allStepKeys.filter(
    (k) => steps[k]?.status === "not_needed",
  ).length;
  const waiting = allStepKeys.filter(
    (k) => steps[k]?.status === "waiting",
  ).length;

  const waitingSteps = allStepKeys
    .filter((k) => steps[k]?.status === "waiting")
    .slice(0, 5)
    .map((k) => {
      const parts = k.replace(`${supplierId}_`, "").split("_");
      const catIdP = parts[0];
      const idxP = parseInt(parts[1]) || 0;
      const cat = PLM_CATEGORIES.find((c) => c.id === catIdP);
      return { k, title: cat?.steps[idxP] || k, cat: cat?.name || "" };
    });

  const FABRICS = ["Woven", "Knitted", "Non-woven"];

  const buyer = orderProfile?.Buyer || {};
  const supplier = Array.isArray(orderProfile?.Suppliers)
    ? orderProfile.Suppliers[0] || {}
    : {};

  const selectedFabric = orderProfile?.fabricDefault || null;
  const products = Array.isArray(supplier?.Products)
    ? supplier.Products.filter(Boolean)
    : [];
  const activeProduct = supplier?.selectedProduct || products[0] || null;

  const orderNumber = orderProfile?.orderNumber || "—";
  const orderMonth = orderProfile?.orderMonth || "—";
  const buyerName = buyer?.name || buyer?.buyerName || "—";
  const buyerLocation = buyer?.location || buyer?.country || "—";
  const supplierName = supplier?.name || supplier?.supplierName || "—";
  const supplierLocation = supplier?.location || supplier?.country || "—";
  const supplierRole = supplier?.role || "Supplier";
  const orderValueUSD =
    orderProfile?.orderValueUSD != null
      ? `$${orderProfile.orderValueUSD.toLocaleString()}`
      : "—";
  const orderValueINR = orderProfile?.orderValueINR || "—";
  const incoterm = orderProfile?.incoterm || "—";
  const skuCount = orderProfile?.skuCount || "—";
  const skuBreakdown = Array.isArray(orderProfile?.skuBreakdown)
    ? orderProfile.skuBreakdown
    : [];
  const paymentMethod = supplier?.paymentMethod || "";
  const paymentTerms = supplier?.paymentTerms || "Payment Terms: —";
  const paymentBank = supplier?.paymentBank || "—";
  const exFactory = supplier?.exFactory || orderProfile?.exFactory || "—";
  const portRoute = orderProfile?.portRoute || "—";

  const deliveryDateLabel = (() => {
    const raw = supplier?.deliveryDate;
    if (!raw) return "—";
    const d = raw?.toDate ? raw.toDate() : new Date(raw);
    if (!d || isNaN(d)) return "—";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  })();

  const timelineCard = (() => {
    const label = supplierName !== "—" ? supplierName : "Supplier";
    const deliveryDate = orderProfile?.deliveryDate;
    const orderStart = orderProfile?.orderStart;
    const delivDate = deliveryDate?.toDate
      ? deliveryDate.toDate()
      : deliveryDate
        ? new Date(deliveryDate)
        : null;
    const startDate = orderStart?.toDate
      ? orderStart.toDate()
      : orderStart
        ? new Date(orderStart)
        : null;
    const circumD = 2 * Math.PI * 30;

    if (!delivDate || !startDate || isNaN(delivDate) || isNaN(startDate)) {
      return {
        label,
        hasDates: false,
        pctElapsed: 0,
        remainingDays: "—",
        deliveryLabel: "—",
        arcColor: "#c8a96e",
        arcOffset: circumD,
        circum: circumD,
      };
    }

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const todayD = new Date();
    const elapsedD = Math.max(0, Math.round((todayD - startDate) / 86400000));
    const totalDaysD = Math.max(
      1,
      Math.round((delivDate - startDate) / 86400000),
    );
    const remainingD = Math.max(0, Math.round((delivDate - todayD) / 86400000));
    const pctElapsedD = Math.min(
      100,
      Math.round((elapsedD / totalDaysD) * 100),
    );
    const arcOffsetD = circumD - (pctElapsedD / 100) * circumD;
    const arcColorD = remainingD < 20 ? "#d07070" : "#c8a96e";

    return {
      label,
      hasDates: true,
      pctElapsed: pctElapsedD,
      remainingDays: remainingD,
      deliveryLabel: `${delivDate.getDate()} ${months[delivDate.getMonth()]} ${delivDate.getFullYear()}`,
      arcColor: arcColorD,
      arcOffset: arcOffsetD,
      circum: circumD,
    };
  })();

  return (
    <>
      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className={styles.dashGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Overall Progress</div>
          <div className={styles.statValue}>
            {pct}
            <span style={{ fontSize: 18 }}>%</span>
          </div>
          <div className={styles.statSub}>
            {done} of {applicable} steps complete
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>In Progress</div>
          <div className={`${styles.statValue} ${styles.statAccent}`}>
            {inProgress}
          </div>
          <div className={styles.statSub}>Active steps</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Not Needed</div>
          <div
            className={`${styles.statValue} ${styles.orderProfileStatGold}`}
            style={{ color: "#6a8a9a" }}
          >
            {notNeeded}
          </div>
          <div className={styles.statSub}>Excluded from flow</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Awaiting Approval</div>
          <div className={`${styles.statValue} ${styles.orderProfileStatWarm}`}>
            {waiting}
          </div>
          <div className={styles.statSub}>Pending sign-off</div>
        </div>
      </div>

      {/* ── PLC Progress panel ──────────────────────────────────────────── */}
      <div className={styles.panel} style={{ marginBottom: 20 }}>
        <div className={styles.panelTitle}>Product Lifecycle Progress</div>
        <div className={styles.plcProgressList}>
          {PLM_CATEGORIES.map((cat) => {
            const keys = cat.steps.map((_, i) =>
              stepDocId(supplierId, cat.id, i),
            );
            const catDone = keys.filter(
              (k) => steps[k]?.status === "done",
            ).length;
            const catApplicable = keys.filter(
              (k) => steps[k]?.status !== "not_needed",
            ).length;
            const catPct =
              catApplicable > 0
                ? Math.round((catDone / catApplicable) * 100)
                : 0;
            return (
              <div key={cat.id} className={styles.plcProgressRow}>
                <span className={styles.plcProgressName}>{cat.name}</span>
                <div className={styles.plcProgressTrack}>
                  <div
                    className={styles.plcProgressFill}
                    style={{ width: `${catPct}%` }}
                  />
                </div>
                <span className={styles.plcProgressPct}>{catPct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Order profile card ──────────────────────────────────────────── */}
      <div className={styles.dashPanels} style={{ gridTemplateColumns: "1fr" }}>
        <div className={`${styles.panel} ${styles.orderProfileCard}`}>
          {/* Top strip */}
          <div className={styles.orderProfileTop}>
            {/* Order number */}
            <div className={styles.orderProfileNumCell}>
              <div className={styles.orderProfileNumLabel}>Order</div>
              <div
                className={`${styles.partyPulse2} ${styles.orderProfileNum}`}
              >
                {orderNumber}
              </div>
              <div className={styles.orderProfileNumSub}>
                Active · {orderMonth}
              </div>
            </div>

            {/* Parties + tags */}
            <div className={styles.orderProfilePartiesCell}>
              <div className={styles.orderProfilePartiesRow}>
                {/* Buyer */}
                <div className={styles.orderProfileParty}>
                  <div className={styles.orderProfilePartyRole}>Buyer</div>
                  <div className={styles.orderProfilePartyName}>
                    {buyerName}
                  </div>
                  <div className={styles.orderProfilePartyLocation}>
                    <div
                      className={`${styles.partyPulse} ${styles.orderProfilePartyDot}`}
                      style={{ background: "#c8a96e" }}
                    />
                    {buyerLocation}
                  </div>
                </div>

                {/* Supplier */}
                <div className={styles.orderProfileParty}>
                  <div className={styles.orderProfilePartyRole}>
                    {supplierRole}
                  </div>
                  <div className={styles.orderProfilePartyName}>
                    {supplierName}
                  </div>
                  <div className={styles.orderProfilePartyLocation}>
                    <div
                      className={`${styles.partyPulse} ${styles.orderProfilePartyDot}`}
                      style={{ background: "#6abf9a" }}
                    />
                    {supplierLocation}
                  </div>
                </div>
              </div>

              {/* Construction + Products tags */}
              <div className={styles.orderProfileTagsRow}>
                <span className={styles.orderProfileTagsLabel}>
                  Construction
                </span>
                {FABRICS.map((f) => (
                  <button
                    key={f}
                    disabled
                    aria-pressed={selectedFabric === f}
                    className={`${styles.orderProfileTag} ${selectedFabric === f ? styles.orderProfileTagActive : ""}`}
                  >
                    {f}
                  </button>
                ))}

                <span className={styles.orderProfileTagDivider} />

                <span className={styles.orderProfileTagsLabel}>Products</span>
                {products.length > 0 ? (
                  products.map((p) => (
                    <button
                      key={p}
                      type="button"
                      disabled
                      className={`${styles.orderProfileTag} ${activeProduct === p ? styles.orderProfileTagActive : ""}`}
                    >
                      {p}
                    </button>
                  ))
                ) : (
                  <button
                    type="button"
                    disabled
                    className={styles.orderProfileTag}
                  >
                    —
                  </button>
                )}
              </div>
            </div>

            {/* Timeline donut */}
            <div className={styles.orderProfileTimelineCell}>
              <div className={styles.orderProfileTimelineLabel}>
                {timelineCard.label}
              </div>
              <div className={styles.orderProfileTimelineSublabel}>
                Days left
              </div>
              <div className={styles.orderProfileDonutWrap}>
                <svg
                  width="66"
                  height="66"
                  viewBox="0 0 72 72"
                  aria-hidden="true"
                  style={{ display: "block" }}
                >
                  <circle
                    cx="36"
                    cy="36"
                    r="30"
                    fill="none"
                    stroke="rgba(200,169,110,0.10)"
                    strokeWidth="5"
                  />
                  <circle
                    cx="36"
                    cy="36"
                    r="30"
                    fill="none"
                    stroke={timelineCard.arcColor}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray={timelineCard.circum}
                    strokeDashoffset={timelineCard.arcOffset}
                    style={{
                      transform: "rotate(-90deg)",
                      transformOrigin: "36px 36px",
                      transition:
                        "stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </svg>
                <div className={styles.orderProfileDonutCenter}>
                  <span
                    className={styles.orderProfileDonutPct}
                    style={{ color: timelineCard.arcColor }}
                  >
                    {timelineCard.hasDates
                      ? `${timelineCard.pctElapsed}%`
                      : "—"}
                  </span>
                </div>
              </div>
              <div
                className={styles.orderProfileDaysValue}
                style={{ color: timelineCard.arcColor }}
              >
                {timelineCard.remainingDays}
              </div>
              <div className={styles.orderProfileDaysDelivery}>
                {timelineCard.deliveryLabel}
              </div>
            </div>
          </div>
          {/* end orderProfileTop */}

          {/* Bottom stats grid */}
          <div className={styles.orderProfileStatsGrid}>
            {/* Order Value */}
            <div className={styles.orderProfileStatCell}>
              <div className={styles.orderProfileStatLabel}>Order Value</div>
              <div className={styles.orderProfileStatValue}>
                {orderValueUSD}
              </div>
              <div
                className={`${styles.orderProfileStatSub} ${styles.orderProfileStatGreen}`}
              >
                {orderValueINR}
              </div>
              <div className={styles.orderProfileStatSub}>{incoterm}</div>
            </div>

            {/* SKUs */}
            <div className={styles.orderProfileStatCell}>
              <div className={styles.orderProfileStatLabel}>SKUs / Items</div>
              <div className={styles.orderProfileStatValue}>{skuCount}</div>
              <div className={styles.orderProfileSkuTags}>
                {skuBreakdown.length > 0 ? (
                  skuBreakdown.map((s) => (
                    <span key={s} className={styles.orderProfileSkuTag}>
                      {s}
                    </span>
                  ))
                ) : (
                  <span className={styles.orderProfileSkuTag}>—</span>
                )}
              </div>
            </div>

            {/* Payment */}
            <div className={styles.orderProfileStatCell}>
              <div className={styles.orderProfileStatLabel}>Payment</div>
              <div
                className={`${styles.orderProfileStatSub} ${styles.orderProfileStatGold}`}
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  marginBottom: 6,
                }}
              >
                {supplierName}
              </div>
              {paymentMethod ? (
                <div className={styles.orderProfilePayBadge}>
                  <span className={styles.orderProfilePayDot} />
                  {paymentMethod}
                </div>
              ) : (
                <div className={styles.orderProfileStatSub}>—</div>
              )}
              <div
                className={styles.orderProfileStatSub}
                style={{ marginTop: 8 }}
              >
                {paymentTerms}
              </div>
              <div className={styles.orderProfileStatSub}>
                Bank: {paymentBank}
              </div>
            </div>

            {/* Delivery */}
            <div className={styles.orderProfileStatCell}>
              <div className={styles.orderProfileStatLabel}>Delivery</div>
              <div
                className={`${styles.orderProfileStatSub} ${styles.orderProfileStatGold}`}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  marginBottom: 6,
                }}
              >
                {supplierRole}
              </div>
              <div
                className={`${styles.orderProfileStatSub} ${styles.orderProfileStatWarm}`}
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                }}
              >
                {deliveryDateLabel}
              </div>
              <div className={styles.orderProfileStatSub}>
                Ex-factory: {exFactory}
              </div>
              <div className={styles.orderProfileStatSub}>
                Port: {portRoute}
              </div>
            </div>
          </div>
          {/* end orderProfileStatsGrid */}
        </div>
      </div>

      {/* ── Lifecycle Analytics panel ───────────────────────────────────── */}
      <div className={styles.panel} style={{ marginBottom: 20 }}>
        <div className={styles.panelTitle}>Lifecycle Analytics</div>

        <div className={styles.analyticsLegend}>
          {[
            { color: "#6abf9a", dash: false, label: "Done" },
            { color: "#c8a96e", dash: false, label: "In Progress" },
            { color: "#dbb866", dash: true, label: "Waiting" },
            { color: "#d07070", dash: true, label: "Not Needed" },
          ].map(({ color, dash, label }) => (
            <span key={label} className={styles.analyticsLegendItem}>
              <svg width="24" height="10" aria-hidden="true">
                {dash ? (
                  <line
                    x1="0"
                    y1="5"
                    x2="24"
                    y2="5"
                    stroke={color}
                    strokeWidth="2"
                    strokeDasharray="4,3"
                  />
                ) : (
                  <line
                    x1="0"
                    y1="5"
                    x2="24"
                    y2="5"
                    stroke={color}
                    strokeWidth="2"
                  />
                )}
                <circle cx="12" cy="5" r="3" fill={color} />
              </svg>
              <span className={styles.analyticsLegendLabel}>{label}</span>
            </span>
          ))}
        </div>

        <div className={styles.analyticsChartWrap}>
          <canvas
            id="plmMultiLineChart"
            role="img"
            aria-label="Multi-line chart showing Done, In Progress, Waiting, and Blocked step counts across all PLM phases"
          >
            Step status counts across Design, Development, Sourcing,
            Manufacturing, Quality, Packaging, Documentation, and Distribution
            phases.
          </canvas>
        </div>

        {(() => {
          const labels = PLM_CATEGORIES.map((c) => c.name.split(" ")[0]);
          const doneData = PLM_CATEGORIES.map(
            (cat) =>
              cat.steps
                .map((_, i) => stepDocId(supplierId, cat.id, i))
                .filter((k) => steps[k]?.status === "done").length,
          );
          const inProgData = PLM_CATEGORIES.map(
            (cat) =>
              cat.steps
                .map((_, i) => stepDocId(supplierId, cat.id, i))
                .filter((k) => steps[k]?.status === "in_progress").length,
          );
          const waitingData = PLM_CATEGORIES.map(
            (cat) =>
              cat.steps
                .map((_, i) => stepDocId(supplierId, cat.id, i))
                .filter((k) => steps[k]?.status === "waiting").length,
          );
          const notNeededData = PLM_CATEGORIES.map(
            (cat) =>
              cat.steps
                .map((_, i) => stepDocId(supplierId, cat.id, i))
                .filter((k) => steps[k]?.status === "not_needed").length,
          );

          const chartConfig = JSON.stringify({
            type: "line",
            data: {
              labels,
              datasets: [
                {
                  label: "Done",
                  data: doneData,
                  borderColor: "#6abf9a",
                  backgroundColor: "#6abf9a",
                  borderWidth: 2,
                  pointRadius: 4,
                  pointHoverRadius: 6,
                  tension: 0.35,
                  fill: false,
                },
                {
                  label: "In Progress",
                  data: inProgData,
                  borderColor: "#c8a96e",
                  backgroundColor: "#c8a96e",
                  borderWidth: 2,
                  pointRadius: 4,
                  pointHoverRadius: 6,
                  tension: 0.35,
                  fill: false,
                },
                {
                  label: "Waiting",
                  data: waitingData,
                  borderColor: "#dbb866",
                  backgroundColor: "#dbb866",
                  borderWidth: 2,
                  pointRadius: 4,
                  pointHoverRadius: 6,
                  tension: 0.35,
                  fill: false,
                },
                {
                  label: "Not Needed",
                  data: notNeededData,
                  borderColor: "#d07070",
                  backgroundColor: "#d07070",
                  borderWidth: 2,
                  pointRadius: 4,
                  pointHoverRadius: 6,
                  tension: 0.35,
                  fill: false,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: "index", intersect: false },
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: "#1f3330",
                  titleColor: "#e8dfc8",
                  bodyColor: "#7a8078",
                  borderColor: "rgba(200,169,110,0.25)",
                  borderWidth: 1,
                  padding: 10,
                },
              },
              scales: {
                x: {
                  ticks: {
                    color: "#7a8078",
                    font: { size: 10, family: "Montserrat, sans-serif" },
                    maxRotation: 35,
                    autoSkip: false,
                  },
                  grid: { color: "rgba(200,169,110,0.06)" },
                  border: { color: "rgba(200,169,110,0.15)" },
                },
                y: {
                  beginAtZero: true,
                  ticks: {
                    color: "#7a8078",
                    font: { size: 12, family: "Montserrat, sans-serif" },
                    stepSize: 1,
                  },
                  grid: { color: "rgba(200,169,110,0.06)" },
                  border: { color: "rgba(200,169,110,0.15)" },
                  title: { display: true, text: "Steps", color: "#7a8078" },
                },
              },
            },
          });

          if (typeof window !== "undefined") {
            const init = () => {
              const canvas = document.getElementById("plmMultiLineChart");
              if (!canvas || !window.Chart) return;
              if (canvas._chartInstance) canvas._chartInstance.destroy();
              const cfg = JSON.parse(chartConfig);
              cfg.data.datasets[2].borderDash = [5, 4];
              cfg.data.datasets[3].borderDash = [5, 4];
              canvas._chartInstance = new window.Chart(canvas, cfg);
            };
            if (window.Chart) {
              setTimeout(init, 0);
            } else {
              const s = document.createElement("script");
              s.src =
                "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
              s.onload = init;
              document.head.appendChild(s);
            }
          }
          return null;
        })()}
      </div>
    </>
  );
}
